import { z } from 'zod';

/**
 * One point-in-time Zillow Zestimate reading.
 *
 * Stored as a list of readings rather than a single number: a Zestimate is a
 * snapshot, and a money figure with no date on it cannot be judged later.
 * `capturedAt` is when Attik fetched the reading; `providerUpdatedAt` is
 * Zillow's own "as of" date when the response carries one.
 *
 * Every numeric field is nullable because Zestimate coverage is uneven — a
 * parcel can resolve with no estimate at all, and "we looked and Zillow has
 * nothing" is different information from "we never looked".
 */
export const PropertyZestimateSchema = z.object({
  /** Primary Zestimate in USD, or `null` when Zillow has no estimate. */
  value: z.number().nullable(),
  /** Bounds of Zillow's confidence range, when supplied. */
  valueLow: z.number().nullable().optional(),
  valueHigh: z.number().nullable().optional(),
  /** Monthly rental Zestimate and its range, when supplied. */
  rentalValue: z.number().nullable().optional(),
  rentalValueLow: z.number().nullable().optional(),
  rentalValueHigh: z.number().nullable().optional(),
  /** Zillow property id the reading was taken against. */
  zpid: z.string().nullable().optional(),
  /** When Attik fetched this reading. */
  capturedAt: z.coerce.date(),
  /** Zillow's own valuation date, when the response carries one. */
  providerUpdatedAt: z.coerce.date().nullable().optional(),
  /**
   * Provenance, so a later provider swap stays distinguishable. Optional
   * rather than defaulted so the schema's input and output types match and it
   * stays usable as a `ZodSchema<T>`; the stored default lives on the model.
   */
  source: z.string().optional(),
});
export type PropertyZestimate = z.infer<typeof PropertyZestimateSchema>;

/**
 * How exactly a property's `lat`/`lng` pins the building.
 *
 * - `address` — a street-level match (a picked suggestion, or the full address
 *   geocoded).
 * - `approximate` — the full address did not resolve; the point is some address
 *   in the same city and zip, not the property.
 * - `postcode` — only the zip resolved; the point is the zip's centre, which can
 *   be miles from the property.
 *
 * Absent on records saved before precision was tracked, and on coordinates
 * written by paths that do not report it — treat absent as unknown, not exact.
 */
export const LOCATION_PRECISIONS = ['address', 'approximate', 'postcode'] as const;
export const LocationPrecisionSchema = z.enum(LOCATION_PRECISIONS);
export type LocationPrecision = z.infer<typeof LocationPrecisionSchema>;

/** Whether a stored location is known to be coarser than the building itself. */
export function isApproximateLocation(
  precision: LocationPrecision | null | undefined,
): boolean {
  return precision === 'approximate' || precision === 'postcode';
}

export const PropertySchema = z.object({
  street: z.string(),
  unit: z.string().nullable().optional(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  lat: z.number(),
  lng: z.number(),
  /** How exactly `lat`/`lng` pins the building; see `LocationPrecision`. */
  locationPrecision: LocationPrecisionSchema.nullable().optional(),
  sqft: z.number().nullable().optional(),
  yearBuilt: z.number().nullable().optional(),
  unitCount: z.number().nullable().optional(),
  foundation: z.string().nullable().optional(),
  /**
   * Zestimate history, newest reading anywhere in the list — read it through
   * `latestPropertyZestimate` rather than indexing. Bounded by
   * `MAX_PROPERTY_ZESTIMATES` on write (see `mergePropertyZestimate`); left
   * unbounded here so an over-long legacy document still parses.
   */
  zestimates: z.array(PropertyZestimateSchema).nullable().optional(),
  address: z.string().optional(),
});
export type Property = z.infer<typeof PropertySchema>;

/**
 * Cap on stored readings. A booking that edits its address repeatedly should
 * not grow the parent document without bound; 24 keeps a couple of years of
 * genuine value changes while staying small.
 */
export const MAX_PROPERTY_ZESTIMATES = 24;

/** Epoch millis for a `Date` or a date string; `NaN` when unusable. */
function readingTime(reading: PropertyZestimate): number {
  const at = reading.capturedAt;
  return at instanceof Date ? at.getTime() : new Date(at).getTime();
}

/**
 * The newest reading by capture time, or `null` when there is none.
 *
 * Readings are appended in order, but ordering is not relied on — documents
 * written by other paths (imports, backfills) may not be sorted.
 */
export function latestPropertyZestimate(
  zestimates: PropertyZestimate[] | null | undefined,
): PropertyZestimate | null {
  if (!zestimates?.length) return null;

  let newest: PropertyZestimate | null = null;
  let newestTime = -Infinity;
  for (const reading of zestimates) {
    const time = readingTime(reading);
    // An unparseable timestamp must not win over a real one.
    if (Number.isNaN(time)) continue;
    if (time >= newestTime) {
      newest = reading;
      newestTime = time;
    }
  }

  if (newest) return newest;

  // Every timestamp was unusable, but the readings themselves are still real:
  // fall back to the last one written.
  return zestimates[zestimates.length - 1] ?? null;
}

/**
 * The newest reading's dollar value, or `null` when unknown.
 *
 * This is the scalar that action-flow conditions compare against, so that a
 * rule reads `zestimate > 750000` the same way it reads `sqft > 4000`.
 */
export function latestPropertyZestimateValue(
  zestimates: PropertyZestimate[] | null | undefined,
): number | null {
  return latestPropertyZestimate(zestimates)?.value ?? null;
}

/**
 * Folds a freshly fetched reading into the stored list.
 *
 * - A reading for a **different** parcel replaces the list. Changing the
 *   address to another house makes the prior readings someone else's value,
 *   not this property's history.
 * - A reading identical in value to the newest one is dropped. The lookup
 *   re-runs on every address edit, and repeating a value carries no new
 *   information about the property.
 * - An empty reading for a parcel we already have a figure for is dropped.
 *   Coverage is uneven and can drop out transiently; a blank answer today is
 *   not a reason to bury a real number from yesterday.
 * - Otherwise the reading is appended and the list trimmed to the newest
 *   `MAX_PROPERTY_ZESTIMATES`.
 */
export function mergePropertyZestimate(
  existing: PropertyZestimate[] | null | undefined,
  incoming: PropertyZestimate | null | undefined,
): PropertyZestimate[] {
  const current = existing ?? [];
  if (!incoming) return current;

  const latest = latestPropertyZestimate(current);
  if (!latest) return [incoming];

  // Both parcels known and different -> different house, start over.
  if (latest.zpid && incoming.zpid && latest.zpid !== incoming.zpid) {
    return [incoming];
  }

  if (latest.value === incoming.value) return current;
  if (incoming.value === null && latest.value !== null) return current;

  const appended = [...current, incoming];
  return appended.length > MAX_PROPERTY_ZESTIMATES
    ? appended.slice(appended.length - MAX_PROPERTY_ZESTIMATES)
    : appended;
}
